/**
 * Calendar layout utilities.
 *
 * Pure functions for timezone-aware date handling, event positioning,
 * track-packing for overlapping events, and status color mapping.
 *
 * @module utils/calendarLayout
 */

import { utcToZonedTime } from 'date-fns-tz'
import { startOfDay, endOfDay, format, parseISO, isSameDay } from 'date-fns'
import { decodeHtmlEntities } from './displayText'

// ---------------------------------------------------------------------------
// Timezone helpers
// ---------------------------------------------------------------------------

/**
 * Extract local hour and minute from an ISO string in a given timezone.
 * @param {string} isoString - UTC ISO date string
 * @param {string} tz - IANA timezone (e.g. 'America/New_York')
 * @returns {{ hour: number, minute: number }}
 */
export function getLocalTimeComponents(isoString, tz) {
  const d = utcToZonedTime(new Date(isoString), tz)
  return { hour: d.getHours(), minute: d.getMinutes() }
}

/**
 * Get the YYYY-MM-DD key for a date in the user's timezone.
 * @param {Date|string} date
 * @param {string} tz
 * @returns {string}
 */
export function toUserTzDayKey(date, tz) {
  const d = typeof date === 'string' ? parseISO(date) : date
  return format(utcToZonedTime(d, tz), 'yyyy-MM-dd')
}

/**
 * Timezone-aware isSameDay comparison.
 * @param {Date|string} a
 * @param {Date|string} b
 * @param {string} tz
 * @returns {boolean}
 */
export function isSameDayTz(a, b, tz) {
  const da = utcToZonedTime(typeof a === 'string' ? parseISO(a) : a, tz)
  const db = utcToZonedTime(typeof b === 'string' ? parseISO(b) : b, tz)
  return isSameDay(da, db)
}

// ---------------------------------------------------------------------------
// End time fallback
// ---------------------------------------------------------------------------

const ONE_HOUR_MS = 60 * 60 * 1000
const FIFTEEN_MIN_MS = 15 * 60 * 1000

/**
 * Get effective end time, falling back to endedAtMs or startTime + 1h.
 * @param {object} meeting
 * @returns {string} ISO string
 */
export function getEffectiveEndTime(meeting) {
  const end = meeting.endTime || meeting.scheduledEndTime
  if (end) return end
  if (meeting.endedAtMs) return new Date(Number(meeting.endedAtMs)).toISOString()
  const start = meeting.startTime || meeting.scheduledStartTime
  if (!start) return new Date().toISOString()
  return new Date(new Date(start).getTime() + ONE_HOUR_MS).toISOString()
}

/**
 * Get display end time with a minimum 15-minute visual duration.
 * Prevents zero-duration events from being invisible on the time grid.
 * @param {object} meeting
 * @returns {string} ISO string
 */
export function getDisplayEndTime(meeting) {
  const start = new Date(meeting.startTime || meeting.scheduledStartTime)
  const end = new Date(getEffectiveEndTime(meeting))
  if (end.getTime() - start.getTime() < FIFTEEN_MIN_MS) {
    return new Date(start.getTime() + FIFTEEN_MIN_MS).toISOString()
  }
  return end.toISOString()
}

// ---------------------------------------------------------------------------
// Grid positioning
// ---------------------------------------------------------------------------

/**
 * Convert an ISO time to a pixel Y offset within a 24-hour grid.
 * @param {string} isoString
 * @param {string} tz
 * @param {number} hourHeight - Pixels per hour
 * @returns {number}
 */
export function timeToY(isoString, tz, hourHeight) {
  const { hour, minute } = getLocalTimeComponents(isoString, tz)
  return (hour + minute / 60) * hourHeight
}

/**
 * Compute top and height for an event block on the time grid.
 * @param {object} meeting
 * @param {string} tz
 * @param {number} hourHeight
 * @returns {{ top: number, height: number }}
 */
export function eventToRect(meeting, tz, hourHeight) {
  const startStr = meeting.startTime || meeting.scheduledStartTime
  const endStr = getDisplayEndTime(meeting)
  const top = timeToY(startStr, tz, hourHeight)
  const bottom = timeToY(endStr, tz, hourHeight)
  return { top, height: Math.max(bottom - top, hourHeight / 4) }
}

// ---------------------------------------------------------------------------
// Track-packing (overlap handling)
// ---------------------------------------------------------------------------

/**
 * Assign track indices to overlapping events for side-by-side layout.
 *
 * @param {Array} events - Already filtered for one column/day
 * @param {string} tz
 * @param {number} hourHeight
 * @returns {Array<{ meeting, top, height, track, totalTracks }>}
 */
export function packTracks(events, tz, hourHeight) {
  if (events.length === 0) return []

  const items = events.map((m) => {
    const rect = eventToRect(m, tz, hourHeight)
    return { meeting: m, ...rect }
  })

  // Sort by top (start time), then height desc (longer events first)
  items.sort((a, b) => a.top - b.top || b.height - a.height)

  // Greedy track assignment
  const trackEnds = [] // trackEnds[i] = Y where track i becomes free
  for (const item of items) {
    let placed = false
    for (let t = 0; t < trackEnds.length; t++) {
      if (trackEnds[t] <= item.top) {
        item.track = t
        trackEnds[t] = item.top + item.height
        placed = true
        break
      }
    }
    if (!placed) {
      item.track = trackEnds.length
      trackEnds.push(item.top + item.height)
    }
  }

  // Compute overlap groups to set totalTracks per group
  // Two items are in the same group if their vertical ranges overlap
  const groups = []
  for (const item of items) {
    let added = false
    for (const group of groups) {
      const overlaps = group.some(
        (g) => g.top < item.top + item.height && g.top + g.height > item.top
      )
      if (overlaps) {
        group.push(item)
        added = true
        break
      }
    }
    if (!added) groups.push([item])
  }

  for (const group of groups) {
    const maxTrack = Math.max(...group.map((g) => g.track)) + 1
    for (const item of group) {
      item.totalTracks = maxTrack
    }
  }

  return items
}

// ---------------------------------------------------------------------------
// Grouping
// ---------------------------------------------------------------------------

/**
 * Group meetings by day (timezone-aware) with ascending sort.
 * Fixes the sort bug where meetings in month cells were unsorted.
 *
 * @param {Array} meetings
 * @param {string} userTz
 * @returns {Map<string, Array>} key = 'YYYY-MM-DD', sorted by start time
 */
export function groupEventsByDay(meetings, userTz) {
  const map = new Map()

  for (const m of meetings) {
    const start = m.startTime ?? m.scheduledStartTime
    if (!start) continue
    const key = toUserTzDayKey(start, userTz)
    if (!map.has(key)) map.set(key, [])
    map.get(key).push(m)
  }

  // Sort each day ascending by start time
  for (const [, dayMeetings] of map) {
    dayMeetings.sort((a, b) => {
      const sa = new Date(a.startTime ?? a.scheduledStartTime).getTime()
      const sb = new Date(b.startTime ?? b.scheduledStartTime).getTime()
      return sa - sb
    })
  }

  return map
}

// ---------------------------------------------------------------------------
// Filtering
// ---------------------------------------------------------------------------

/**
 * Check if a meeting is visible on a given day (multi-day aware, timezone-correct).
 * @param {object} meeting
 * @param {Date} day - The day to check
 * @param {string} userTz
 * @returns {boolean}
 */
export function meetingVisibleOnDay(meeting, day, userTz) {
  const startStr = meeting.startTime ?? meeting.scheduledStartTime
  if (!startStr) return false
  const mStart = utcToZonedTime(new Date(startStr), userTz)
  const mEnd = utcToZonedTime(new Date(getEffectiveEndTime(meeting)), userTz)
  const dayInTz = utcToZonedTime(day, userTz)
  const ds = startOfDay(dayInTz)
  const de = endOfDay(dayInTz)
  return mStart < de && mEnd > ds
}

// ---------------------------------------------------------------------------
// Status colors
// ---------------------------------------------------------------------------

const STATUS_COLORS = {
  created:  { bg: 'bg-primary-50',  text: 'text-primary-700',  border: 'border-primary-300',  dot: 'bg-primary-500' },
  starting: { bg: 'bg-amber-50',    text: 'text-amber-700',    border: 'border-amber-300',    dot: 'bg-amber-500' },
  live:     { bg: 'bg-green-50',    text: 'text-green-700',    border: 'border-green-300',    dot: 'bg-green-500' },
  paused:   { bg: 'bg-purple-50',   text: 'text-purple-700',   border: 'border-purple-300',   dot: 'bg-purple-500' },
  ending:   { bg: 'bg-orange-50',   text: 'text-orange-700',   border: 'border-orange-300',   dot: 'bg-orange-500' },
  ended:    { bg: 'bg-gray-50',     text: 'text-gray-500',     border: 'border-gray-200',     dot: 'bg-gray-400' },
  error:    { bg: 'bg-red-50',      text: 'text-red-700',      border: 'border-red-300',      dot: 'bg-red-500' },
}

const DEFAULT_COLOR = STATUS_COLORS.created

/**
 * Get Tailwind color classes for a meeting status.
 * @param {string} status
 * @param {boolean} isDraft
 * @returns {{ bg: string, text: string, border: string, dot: string, draftClass: string }}
 */
export function getStatusColor(status, isDraft) {
  const base = STATUS_COLORS[status] ?? DEFAULT_COLOR
  return {
    ...base,
    draftClass: isDraft ? 'border-dashed opacity-70' : '',
  }
}

// ---------------------------------------------------------------------------
// Null-safe title
// ---------------------------------------------------------------------------

/**
 * Get a display-safe meeting title.
 * @param {object} meeting
 * @returns {string}
 */
export function getMeetingTitle(meeting) {
  return decodeHtmlEntities(meeting?.title) || 'Untitled Meeting'
}
